/**
 * PromoAdminCard — Campaign management widget for the admin Pricing panel.
 * Shows: window date-range picker, budget controls, live stats,
 * cost analysis for 1000 users.
 */
import { useState, useEffect } from 'react';
import { Calendar, DollarSign, Users, Loader2, RefreshCw, Save, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';

export function PromoAdminCard() {
  const [cfg,       setCfg]       = useState(null);
  const [analysis,  setAnalysis]  = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [loading,   setLoading]   = useState(true);
  const [form,      setForm]      = useState({
    start:            '2026-10-19',
    end:              '2026-12-31',
    campaign_name:    "Autumn 2026 Writer's Launch",
    global_budget_usd: 500,
    per_user_tokens:  75000,
  });

  async function load() {
    setLoading(true);
    try {
      const [cfgRes, anaRes] = await Promise.all([
        fetch('/api/admin/promo',               { credentials: 'include' }),
        fetch('/api/admin/plans/cost-analysis', { credentials: 'include' }),
      ]);
      const cfgData = await cfgRes.json();
      const anaData = await anaRes.json();
      setCfg(cfgData);
      setAnalysis(anaData);
      if (cfgData.window) setForm({ ...cfgData.window });
    } catch {
      toast.error('Could not load promo config');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleSave() {
    setSaving(true);
    try {
      const res = await fetch('/api/admin/promo', {
        method:      'PUT',
        credentials: 'include',
        headers:     { 'Content-Type': 'application/json' },
        body:        JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success('Promo window updated');
      await load();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  }

  const isActive = cfg?.is_active;

  return (
    <div
      data-testid="promo-admin-card"
      className="rounded-2xl border border-emerald-700/40 bg-gradient-to-br from-emerald-950/60 to-zinc-900/80 p-6 space-y-5"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-semibold text-white">Promo Campaign</h3>
            <Badge
              className={cn(
                'text-[10px] px-1.5 py-0',
                isActive ? 'bg-emerald-600/30 text-emerald-300 border-emerald-600/40'
                         : 'bg-zinc-700/40 text-zinc-400 border-zinc-600/30',
              )}
            >
              {loading ? '…' : isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          <p className="text-xs text-zinc-400">
            New signups within the window auto-receive the Writer's Launch tier.
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={load} className="text-zinc-500 hover:text-white shrink-0">
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
        </Button>
      </div>

      {/* Live stats */}
      {cfg && (
        <div className="grid grid-cols-2 gap-3">
          <Stat icon={<Users className="w-3.5 h-3.5 text-emerald-400" />}
            label="Active Promo Users" value={cfg.promo_users} />
          <Stat icon={<Users className="w-3.5 h-3.5 text-amber-400" />}
            label="Expired → Free" value={cfg.expired_users} />
        </div>
      )}

      {/* Campaign window form */}
      <div className="space-y-3">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Campaign Window</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="space-y-1">
            <span className="text-xs text-zinc-400">Start date</span>
            <Input
              type="date"
              data-testid="promo-start-date"
              value={form.start}
              onChange={e => setForm(f => ({ ...f, start: e.target.value }))}
              className="bg-zinc-800 border-zinc-700 text-zinc-200 text-sm"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-zinc-400">End date</span>
            <Input
              type="date"
              data-testid="promo-end-date"
              value={form.end}
              onChange={e => setForm(f => ({ ...f, end: e.target.value }))}
              className="bg-zinc-800 border-zinc-700 text-zinc-200 text-sm"
            />
          </label>
        </div>
        <label className="block space-y-1">
          <span className="text-xs text-zinc-400">Campaign name</span>
          <Input
            data-testid="promo-campaign-name"
            value={form.campaign_name}
            onChange={e => setForm(f => ({ ...f, campaign_name: e.target.value }))}
            className="bg-zinc-800 border-zinc-700 text-zinc-200 text-sm"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="space-y-1">
            <span className="text-xs text-zinc-400">Global budget (USD)</span>
            <div className="relative">
              <DollarSign className="absolute left-3 top-2.5 w-3.5 h-3.5 text-zinc-500" />
              <Input
                type="number"
                min={0}
                data-testid="promo-budget"
                value={form.global_budget_usd}
                onChange={e => setForm(f => ({ ...f, global_budget_usd: parseFloat(e.target.value) || 0 }))}
                className="bg-zinc-800 border-zinc-700 text-zinc-200 text-sm pl-8"
              />
            </div>
          </label>
          <label className="space-y-1">
            <span className="text-xs text-zinc-400">Tokens per user</span>
            <Input
              type="number"
              min={0}
              data-testid="promo-tokens"
              value={form.per_user_tokens}
              onChange={e => setForm(f => ({ ...f, per_user_tokens: parseInt(e.target.value) || 0 }))}
              className="bg-zinc-800 border-zinc-700 text-zinc-200 text-sm"
            />
          </label>
        </div>
        <Button
          size="sm"
          data-testid="promo-save-btn"
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-emerald-700 hover:bg-emerald-600 text-white"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
          Save Campaign Settings
        </Button>
      </div>

      {/* Cost analysis */}
      {analysis && (
        <div className="space-y-3 pt-1 border-t border-zinc-700/40">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 pt-1">
            <TrendingUp className="w-3 h-3" /> Cost Analysis — 1,000 Users
          </p>
          <div className="grid grid-cols-2 gap-3">
            <CostRow label="Per user (cached)" value={`$${analysis.per_user.cost_cached_usd.toFixed(4)}`} highlight />
            <CostRow label="Per user (no cache)" value={`$${analysis.per_user.cost_no_cache_usd.toFixed(4)}`} />
            <CostRow label="1,000 users (cached)" value={`$${analysis.campaign.total_cost_cached_usd.toFixed(2)}`} highlight />
            <CostRow label="1,000 users (no cache)" value={`$${analysis.campaign.total_cost_no_cache_usd.toFixed(2)}`} />
            <CostRow label="Budget covers" value={`${analysis.campaign.budget_covers_users.toLocaleString()} users`} />
            <CostRow label="Budget utilisation" value={`${analysis.campaign.budget_utilisation_pct}%`}
              warn={analysis.campaign.budget_utilisation_pct > 80} />
          </div>
          <p className="text-[10px] text-zinc-600">
            Model: {analysis.assumptions.model} · {analysis.assumptions.target_words.toLocaleString()} words ·
            {analysis.assumptions.cache_discount_pct}% cache discount · 60/40 in/out split
          </p>
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="rounded-lg bg-zinc-800/50 px-3 py-2 flex items-center gap-2">
      {icon}
      <div>
        <div className="text-lg font-bold text-white leading-none">{value ?? '—'}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">{label}</div>
      </div>
    </div>
  );
}

function CostRow({ label, value, highlight, warn }) {
  return (
    <div className="rounded-lg bg-zinc-800/50 px-3 py-2">
      <div className={cn(
        'text-sm font-mono font-semibold',
        warn ? 'text-amber-400' : highlight ? 'text-emerald-300' : 'text-zinc-300',
      )}>
        {value}
      </div>
      <div className="text-[10px] text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}
